import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiV2 } from "api/firebase";
import { RequestKey } from "api/request-key";
import {
  INotificationBaseMessage,
  IPayloadUpdatedSupplierNotificationBar,
  NotificationType,
} from "@evennt-tech/api-v2-core";

interface UseSupplierNotificationBarStatusResult {
  isUnreadNotification: boolean;
  isNewNotifications: boolean;
  refetch: () => Promise<unknown>;
}

/**
 * Supplier-scoped counterpart of `useNotificationBarStatus`, kept live via the
 * `UPDATED_SUPPLIER_NOTIFICATION_BAR` subscription.
 */
export const useSupplierNotificationBarStatus = (
  supplierCompanyId: string
): UseSupplierNotificationBarStatusResult => {
  const { data, refetch } = useQuery({
    queryKey: [
      RequestKey.GetNotificationBarCount,
      "supplier",
      supplierCompanyId,
    ],
    queryFn: () => apiV2.notificationBar.getCount({ supplierCompanyId }),
    enabled: !!supplierCompanyId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const isUnreadNotification = Boolean(data?.unread);
  const isNewNotifications = Boolean(data?.opened) || Boolean(data?.unread);

  useEffect(() => {
    if (!supplierCompanyId) return undefined;

    const listenerId = "count_unread_supplier_notification_bars";

    // No showId on this event — the api broadcasts it and we filter on the
    // payload.
    apiV2.notification.subscribe<
      INotificationBaseMessage<IPayloadUpdatedSupplierNotificationBar>
    >({
      key: NotificationType.UPDATED_SUPPLIER_NOTIFICATION_BAR,
      listenerId,
      callback: async ({ payload }) => {
        if (payload?.supplierCompanyId === supplierCompanyId) {
          await refetch();
        }
      },
    });

    return () => {
      apiV2.notification.unsubscribe({
        key: NotificationType.UPDATED_SUPPLIER_NOTIFICATION_BAR,
        listenerId,
      });
    };
  }, [supplierCompanyId, refetch]);

  return {
    isUnreadNotification,
    isNewNotifications,
    refetch,
  };
};

export default useSupplierNotificationBarStatus;
